import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Check,
  ChevronRight,
  GraduationCap,
  MapPin,
  Menu,
  MessageCircle,
  Play,
  Quote,
  Sparkles,
  Target,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { navigate } from "./App";
import Logo from "./components/Logo";

const courses = [
  {
    level: "LỚP 10",
    title: "Vững nền tư duy",
    description: "Làm chủ đại số, hình học và phương pháp trình bày ngay từ năm đầu THPT.",
    meta: "2 buổi / tuần",
    color: "mint",
    icon: "x²",
  },
  {
    level: "LỚP 11",
    title: "Bứt phá năng lực",
    description: "Hệ thống kiến thức trọng tâm, tăng tốc kỹ năng giải và tư duy biến đổi.",
    meta: "2 buổi / tuần",
    color: "sun",
    icon: "sin",
  },
  {
    level: "LỚP 12",
    title: "Chinh phục kỳ thi",
    description: "Lộ trình cá nhân hóa, luyện đề chiến lược và tối ưu từng mốc điểm mục tiêu.",
    meta: "3 buổi / tuần",
    color: "coral",
    icon: "∫",
  },
];

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "Thầy Linh dạy Toán | Học Toán bằng tư duy";
  }, []);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="landing">
      <header className="landing-nav">
        <a href="/" onClick={(event) => event.preventDefault()} aria-label="Trang chủ">
          <Logo />
        </a>
        <nav className={menuOpen ? "is-open" : ""} aria-label="Điều hướng chính">
          <button onClick={() => scrollTo("ve-thay")}>Về thầy</button>
          <button onClick={() => scrollTo("phuong-phap")}>Phương pháp</button>
          <button onClick={() => scrollTo("khoa-hoc")}>Khóa học</button>
          <button onClick={() => navigate("/dang-nhap")} className="nav-login">
            Đăng nhập <ArrowRight size={16} />
          </button>
        </nav>
        <button className="menu-toggle" aria-label="Mở menu" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <X /> : <Menu />}
        </button>
      </header>

      <main>
        <section className="hero" id="ve-thay">
          <div className="hero__grain" />
          <div className="hero__content reveal">
            <div className="eyebrow"><Sparkles size={15} /> Toán học dễ hiểu, tiến bộ nhìn thấy</div>
            <h1>
              Học Toán bằng <em>tư duy,</em><br />
              không bằng nỗi sợ.
            </h1>
            <p>
              Tôi là <strong>Thầy Linh</strong>, giáo viên Toán THPT tại TP. Hồ Chí Minh. Tôi giúp học sinh
              biến những công thức khó nhớ thành một hệ thống dễ hiểu và dùng được.
            </p>
            <div className="hero__actions">
              <button className="btn btn--primary" onClick={() => scrollTo("khoa-hoc")}>
                Khám phá khóa học <ArrowRight size={18} />
              </button>
              <button className="btn btn--ghost" onClick={() => scrollTo("phuong-phap")}>
                <span className="play"><Play size={14} fill="currentColor" /></span> Cách thầy giảng
              </button>
            </div>
            <div className="hero__proof">
              <div className="avatar-stack" aria-hidden="true">
                <span>MA</span><span>HN</span><span>TB</span><span>+99</span>
              </div>
              <p><b>Hàng trăm học sinh</b><br />đã tìm lại sự tự tin với Toán</p>
            </div>
          </div>

          <div className="hero__visual reveal reveal--delay">
            <div className="math-board">
              <div className="math-board__top">
                <span className="board-dot" />
                <span>HÀM SỐ & ĐẠO HÀM</span>
                <span>01 / 04</span>
              </div>
              <div className="math-board__graph" aria-label="Minh họa đồ thị hàm số">
                <svg viewBox="0 0 520 330" role="img">
                  <defs>
                    <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
                      <path d="M 32 0 L 0 0 0 32" fill="none" stroke="rgba(255,255,255,.08)" strokeWidth="1" />
                    </pattern>
                  </defs>
                  <rect width="520" height="330" fill="url(#grid)" />
                  <path d="M40 244 H480 M257 28 V298" stroke="rgba(255,255,255,.45)" strokeWidth="1.4" />
                  <path d="M78 275 C145 265, 165 70, 252 164 S385 252, 456 48" fill="none" stroke="#f5d38b" strokeWidth="5" strokeLinecap="round" />
                  <circle cx="252" cy="164" r="8" fill="#ec6b50" />
                  <path d="M218 132 L285 196" stroke="#ec6b50" strokeWidth="2" strokeDasharray="6 6" />
                  <text x="301" y="184" fill="white" fontSize="14">y = f(x)</text>
                  <text x="330" y="80" fill="#f5d38b" fontSize="15">f'(x) &gt; 0</text>
                  <text x="76" y="70" fill="rgba(255,255,255,.75)" fontSize="17">lim f(x) = +∞</text>
                </svg>
              </div>
              <div className="math-board__footer">
                <span>Quan sát • Đặt câu hỏi • Tự kết luận</span>
                <div><i className="active" /><i /><i /><i /></div>
              </div>
            </div>
            <div className="float-card float-card--score">
              <span>TIẾN BỘ GẦN ĐÂY</span>
              <strong>+2.4 điểm</strong>
              <div><i style={{ width: "78%" }} /></div>
            </div>
            <div className="float-card float-card--formula">Δ = b² − 4ac</div>
            <div className="orbit orbit--one" />
            <div className="orbit orbit--two" />
          </div>
        </section>

        <section className="belief-strip" aria-label="Giá trị giảng dạy">
          <div><span>01</span><b>Hiểu bản chất</b><small>Thay vì học thuộc lòng</small></div>
          <div><span>02</span><b>Lộ trình rõ ràng</b><small>Biết mình đang ở đâu</small></div>
          <div><span>03</span><b>Phản hồi liên tục</b><small>Tiến bộ qua từng buổi</small></div>
        </section>

        <section className="method section" id="phuong-phap">
          <div className="section-heading">
            <div>
              <span className="section-kicker">PHƯƠNG PHÁP CỦA THẦY</span>
              <h2>Mỗi bài toán là một<br /><em>câu chuyện có logic.</em></h2>
            </div>
            <p>Không mẹo vặt rời rạc. Học sinh được dẫn dắt từ câu hỏi “vì sao” đến cách tự tìm ra lời giải.</p>
          </div>
          <div className="method-grid">
            <article className="method-feature">
              <div className="method-feature__visual">
                <span className="mini-label">BÀI HỌC HÔM NAY</span>
                <div className="formula-line"><span>f(x)</span><i>=</i><strong>ax² + bx + c</strong></div>
                <svg viewBox="0 0 560 210" aria-hidden="true">
                  <path d="M30 185 Q180 -90 330 185" fill="none" stroke="#eff7f4" strokeWidth="4" />
                  <path d="M40 165 H520" stroke="rgba(255,255,255,.35)" />
                  <circle cx="181" cy="28" r="7" fill="#ef7455" />
                  <path d="M181 28 V166" stroke="#ef7455" strokeDasharray="5 7" />
                </svg>
              </div>
              <div className="method-feature__copy">
                <span>01 — TRỰC QUAN HÓA</span>
                <h3>Nhìn thấy điều công thức muốn nói</h3>
                <p>Đồ thị, mô hình và ví dụ thực tế giúp kiến thức không còn trừu tượng.</p>
              </div>
            </article>
            <div className="method-small">
              <article>
                <div className="method-icon"><Target /></div>
                <span>02 — CÁ NHÂN HÓA</span>
                <h3>Đúng chỗ yếu,<br />đúng tốc độ</h3>
                <p>Bài tập được chọn theo năng lực và mục tiêu điểm số của từng bạn.</p>
              </article>
              <article>
                <div className="method-icon method-icon--sun"><BarChart3 /></div>
                <span>03 — ĐO LƯỜNG</span>
                <h3>Tiến bộ bằng<br />dữ liệu thật</h3>
                <p>Mỗi cột điểm là một tín hiệu để điều chỉnh chiến lược học tập.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="courses section" id="khoa-hoc">
          <div className="section-heading section-heading--center">
            <div>
              <span className="section-kicker">LỘ TRÌNH THPT</span>
              <h2>Chọn đúng lộ trình,<br /><em>đi nhanh hơn.</em></h2>
            </div>
            <p>Chương trình bám sát kiến thức trên lớp, đồng thời mở rộng tư duy theo mục tiêu riêng.</p>
          </div>
          <div className="course-grid">
            {courses.map((course, index) => (
              <article className={`course-card course-card--${course.color}`} key={course.level}>
                <div className="course-card__top">
                  <span>{course.level}</span>
                  <div className="course-symbol">{course.icon}</div>
                </div>
                <div className="course-card__body">
                  <h3>{course.title}</h3>
                  <p>{course.description}</p>
                  <div className="course-meta"><BookOpen size={16} /> {course.meta}</div>
                  <button onClick={() => document.getElementById("lien-he")?.scrollIntoView({ behavior: "smooth" })}>
                    Xem lộ trình <ChevronRight size={17} />
                  </button>
                </div>
                <span className="course-index">0{index + 1}</span>
              </article>
            ))}
          </div>
        </section>

        <section className="story section">
          <div className="story__quote">
            <Quote />
            <blockquote>“Khi một học sinh nói <em>em hiểu rồi</em>, đó luôn là khoảnh khắc đẹp nhất của việc dạy học.”</blockquote>
            <div className="signature">Linh<span>— Giáo viên Toán THPT</span></div>
          </div>
          <div className="story__facts">
            <div><GraduationCap /><b>THPT</b><span>Chuyên môn giảng dạy</span></div>
            <div><MapPin /><b>TP. HCM</b><span>Khu vực giảng dạy</span></div>
            <div><MessageCircle /><b>1:1</b><span>Phản hồi cá nhân</span></div>
          </div>
        </section>

        <section className="cta" id="lien-he">
          <div className="cta__math">π <span>∑</span> √ <i>∞</i></div>
          <div>
            <span className="section-kicker">BẮT ĐẦU TỪ HÔM NAY</span>
            <h2>Sẵn sàng để Toán<br />trở nên <em>dễ hiểu?</em></h2>
            <p>Để lại lời nhắn, thầy sẽ cùng bạn xác định mục tiêu và lộ trình phù hợp.</p>
            <a className="btn btn--cream" href="#khoa-hoc">
              Xem lại các khóa học <ArrowRight size={18} />
            </a>
          </div>
          <ul>
            <li><Check /> Trao đổi mục tiêu miễn phí</li>
            <li><Check /> Đánh giá năng lực đầu vào</li>
            <li><Check /> Gợi ý lộ trình cá nhân</li>
          </ul>
        </section>
      </main>

      <footer className="landing-footer">
        <Logo light />
        <p>Học chắc bản chất. Tiến bộ bền vững.</p>
        <button onClick={() => navigate("/dang-nhap")}>Cổng giáo viên <ArrowRight size={15} /></button>
        <small>© {new Date().getFullYear()} Thầy Linh dạy Toán</small>
      </footer>
    </div>
  );
}
